// [S-090c / S-320c] Contract Classification data hooks.
//
// Backs the shared <ClassificationCatalog> used by two screens:
//   - platform scope → Classification.tsx (platform_admin, /classification)
//     over the S-090b routes under `/platform/classification`.
//   - tenant scope   → TenantClassification.tsx (tenant_admin,
//     /settings/classification) over the S-320b routes under
//     `/tenant/classification`.
//
// The two route families differ slightly (tenant create carries the axis
// in the body; tenant deprecate is POST not PATCH); the scope discriminator
// hides that from callers. Tenant scope is resolved server-side from the
// caller's studioId — the client never passes a tenantId.
//
// Read paths are open to every authenticated caller server-side; the
// screens still gate the write surface with <RequireRole>.
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "./api";

/** Which catalog a hook targets. Drives the route prefix + query keys. */
export type CatalogScope = "platform" | "tenant";

const basePath = (scope: CatalogScope): string =>
  scope === "tenant" ? "/tenant/classification" : "/platform/classification";

// ─── Types — mirror the wire format the admin route emits ──────────────
export type ClassificationAxis =
  | "category"
  | "contract_type"
  | "cba"
  | "facet"
  | "legacy_alias";

export type ClassificationFacetAxis =
  | "medium"
  | "guild"
  | "document_form"
  | "budget_tier"
  | "talent_tier";

export type ClassificationEntryWire = {
  axis: ClassificationAxis;
  id: string;
  label: string;
  description: string | null;
  categoryId: string | null;
  shortForm: boolean | null;
  aliases: string[];
  guildId: string | null;
  effectiveFrom: string | null; // ISO
  facetAxis: ClassificationFacetAxis | null;
  aliasFor: string | null;
  effectiveTo: string | null; // ISO; null = active
  successorId: string | null;
  metadata: Record<string, string>;
};

export type ClassificationListResponse = {
  axis: ClassificationAxis;
  entries: ClassificationEntryWire[];
};

export const classificationKey = (
  scope: CatalogScope,
  axis: ClassificationAxis,
  facetAxis?: ClassificationFacetAxis
): (string | undefined)[] => [scope, "classification", axis, facetAxis];

export function useClassificationList(
  scope: CatalogScope,
  axis: ClassificationAxis,
  facetAxis?: ClassificationFacetAxis,
  // S-320d — consumers that read on behalf of a specific tenant (the upload
  // portal, where a platform_admin uploads for a picked tenant) pass an
  // explicit tenantId; tenant-role callers omit it and the server derives the
  // scope from their studioId. `enabled` gates the query until the tenant is
  // known (a platform_admin before picking).
  opts?: { tenantId?: string; enabled?: boolean }
): UseQueryResult<ClassificationListResponse, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...classificationKey(scope, axis, facetAxis), opts?.tenantId],
    enabled: opts?.enabled ?? true,
    // keep prior rows visible while a filter/scope change refetches (no blank flash)
    placeholderData: keepPreviousData,
    queryFn: () => {
      const params = new URLSearchParams({ axis });
      if (facetAxis !== undefined) params.set("facetAxis", facetAxis);
      if (scope === "tenant" && opts?.tenantId !== undefined) {
        params.set("tenantId", opts.tenantId);
      }
      return api<ClassificationListResponse>(
        `${basePath(scope)}?${params.toString()}`
      );
    },
  });
}

export type CreateClassificationInput = {
  axis: ClassificationAxis;
  id: string;
  label: string;
  description: string | null;
  categoryId?: string | null;
  shortForm?: boolean | null;
  aliases?: string[];
  guildId?: string | null;
  effectiveFrom?: string | null; // ISO
  facetAxis?: ClassificationFacetAxis | null;
  aliasFor?: string | null;
};

export function useCreateClassificationEntry(
  scope: CatalogScope
): UseMutationResult<ClassificationEntryWire, Error, CreateClassificationInput> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      // Platform takes the axis in the path; tenant takes it in the body
      // (the collection-root POST). Same payload otherwise.
      if (scope === "tenant") {
        return await api<ClassificationEntryWire>(basePath(scope), {
          method: "POST",
          body: input,
        });
      }
      const { axis, ...rest } = input;
      return await api<ClassificationEntryWire>(
        `${basePath(scope)}/${encodeURIComponent(axis)}`,
        { method: "POST", body: rest }
      );
    },
    onSuccess: (entry) => {
      const facetAxis = entry.facetAxis !== null ? entry.facetAxis : undefined;
      void qc.invalidateQueries({
        queryKey: classificationKey(scope, entry.axis, facetAxis),
      });
    },
  });
}

export type DeprecateClassificationInput = {
  axis: ClassificationAxis;
  facetAxis?: ClassificationFacetAxis;
  id: string;
  asOf: string; // ISO
  successorId: string | null;
  /** S-090d — cascade dependents when set true and successorId is null. */
  cascade?: boolean;
};

/** S-090d — wire shape extends the entry response with the list of
 *  dependents the server cascaded. Empty array when the deprecation
 *  didn't trigger a cascade. */
export type DeprecateClassificationResponse = ClassificationEntryWire & {
  cascadedDependents: ClassificationDependent[];
};

export type ClassificationDependent = {
  axis: ClassificationAxis;
  id: string;
  relation: "categoryId" | "aliasFor" | "successorId" | "guildId";
};

export type ClassificationDependentsResponse = {
  axis: ClassificationAxis;
  entryId: string;
  dependents: ClassificationDependent[];
};

export function useDeprecateClassificationEntry(
  scope: CatalogScope
): UseMutationResult<
  DeprecateClassificationResponse,
  Error,
  DeprecateClassificationInput
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ axis, id, asOf, successorId, cascade }) => {
      const body: Record<string, unknown> = { asOf, successorId };
      if (cascade !== undefined) body["cascade"] = cascade;
      // Platform deprecate is PATCH; tenant deprecate is POST (S-320b).
      return await api<DeprecateClassificationResponse>(
        `${basePath(scope)}/${encodeURIComponent(axis)}/${encodeURIComponent(id)}/deprecate`,
        { method: scope === "tenant" ? "POST" : "PATCH", body }
      );
    },
    onSuccess: (entry, vars) => {
      void qc.invalidateQueries({
        queryKey: classificationKey(scope, entry.axis, vars.facetAxis),
      });
    },
  });
}

/** S-090d — cosmetic patch on an active entry. */
export type UpdateClassificationInput = {
  axis: ClassificationAxis;
  facetAxis?: ClassificationFacetAxis;
  id: string;
  patch: {
    label?: string;
    description?: string | null;
    aliases?: string[];
    shortForm?: boolean;
    effectiveFrom?: string; // ISO
    metadata?: Record<string, string>;
  };
};

export function useUpdateClassificationEntry(
  scope: CatalogScope
): UseMutationResult<ClassificationEntryWire, Error, UpdateClassificationInput> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ axis, id, patch }) => {
      return await api<ClassificationEntryWire>(
        `${basePath(scope)}/${encodeURIComponent(axis)}/${encodeURIComponent(id)}`,
        { method: "PATCH", body: patch }
      );
    },
    onSuccess: (entry, vars) => {
      void qc.invalidateQueries({
        queryKey: classificationKey(scope, entry.axis, vars.facetAxis),
      });
    },
  });
}

/** S-090d — preflight dependents lookup for the deprecate-confirm UI. */
export function useClassificationDependents(
  scope: CatalogScope,
  axis: ClassificationAxis,
  id: string,
  enabled: boolean
): UseQueryResult<ClassificationDependentsResponse, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [scope, "classification", "dependents", axis, id],
    queryFn: () =>
      api<ClassificationDependentsResponse>(
        `${basePath(scope)}/${encodeURIComponent(axis)}/${encodeURIComponent(id)}/dependents`
      ),
    enabled,
  });
}

// ─── S-320c — tenant fork lifecycle (mode / reseed / inherit) ───────────

export type TenantClassificationModeWire = {
  tenantId: string;
  forked: boolean;
  seededAt: string | null; // ISO
  counts: number;
};

export const tenantClassificationModeKey = [
  "tenant",
  "classification",
  "mode",
] as const;

export function useTenantClassificationMode(): UseQueryResult<
  TenantClassificationModeWire,
  Error
> {
  const api = useApi("admin");
  return useQuery({
    queryKey: tenantClassificationModeKey,
    queryFn: () =>
      api<TenantClassificationModeWire>("/tenant/classification/mode"),
  });
}

/** Re-seed (fork) — destructive replace. The destructive-confirm modal is
 *  the UI gate; the route still requires `confirm:true`. */
export function useReseedTenantClassification(): UseMutationResult<
  TenantClassificationModeWire,
  Error,
  void
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<TenantClassificationModeWire>("/tenant/classification/reseed", {
        method: "POST",
        body: { confirm: true },
      }),
    onSuccess: (mode) => {
      qc.setQueryData(tenantClassificationModeKey, mode);
      // The tenant catalog changed shape (now its own copy) — drop every
      // cached tenant list so each axis refetches against the fork.
      void qc.invalidateQueries({ queryKey: ["tenant", "classification"] });
    },
  });
}

/** Revert to inherited — wipes the tenant copy, reads fall back to platform. */
export function useRevertTenantClassification(): UseMutationResult<
  TenantClassificationModeWire,
  Error,
  void
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<TenantClassificationModeWire>("/tenant/classification/inherit", {
        method: "POST",
        body: {},
      }),
    onSuccess: (mode) => {
      qc.setQueryData(tenantClassificationModeKey, mode);
      void qc.invalidateQueries({ queryKey: ["tenant", "classification"] });
    },
  });
}
